import Vector from "victor";
import { Tool } from "@/wbcontroller/types/Tool";
import ToolType from "@/wbcontroller/types/ToolType";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import { PenlikeShape2 } from "./PenShape";
import { WBHUDState } from "@/store/WBHUDState";
import { Scope } from "@/wbcontroller/types/Scope";
import {
  SimplePointerEventHandler,
  SimplePointerEventHandlerDelegate,
} from "../SimplePointerEventHandler";
import AddPenOperation from "./AddPenOperation";
import { AttributeKey } from "../types/attributes";

/*
 * Quick note about points and vectors.
 *
 * Points should always be stored in JSON as [number, number][] because
 * it's small, but points are often represented in code as Vector objects
 * from the Victor library to make transformations easier.
 */

export class PenTool implements Tool, SimplePointerEventHandlerDelegate {
  // Discriminated union marker
  toolType = ToolType.Pen;

  // Populated by bind()
  wb?: WhiteboardControllerInterface;
  eventHandler: SimplePointerEventHandler;

  // Temporary state during an interaction
  isPointerDown = false;
  // onPointerMove replaces this value each time it fires
  lastScreenSpacePoint = new Vector(0, 0);
  animationFrameHandle = 0;

  // Shape info
  shapeID = "";
  shape: PenlikeShape2;

  // New shapes are copied from this object
  shapePrototype: PenlikeShape2 = {
    shapeType: ShapeType.Pen,
    isOpen: true,
    alpha: 1,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
    points: [],
  };

  constructor() {
    this.eventHandler = new SimplePointerEventHandler();
    // appease typescript
    this.shape = Object.assign({}, this.shapePrototype);
  }

  // Settings passed down from
  // Vuex -> Whiteboard.vue -> WhiteboardController -> this method
  settingsDidChange(settings: WBHUDState) {
    this.shapePrototype.strokeColor = settings.strokeColor;
    this.shapePrototype.strokeWidth = settings.strokeWidth;
  }

  nextZIndexDidChange(nextZIndex: number) {
    this.shapePrototype.zIndex = nextZIndex;
  }

  onPointerDown = (point: Vector) => {
    if (!this.wb) return;
    this.isPointerDown = true;

    this.lastScreenSpacePoint = point;

    // Set up pen shape to be used while user is actively drawing
    this.shapeID = this.wb.makeID();
    const origin = this.wb.camera.convertScreenToWorld(new Vector(0, 0));
    this.shape = Object.assign({}, this.shapePrototype, {
      points: [this.lastScreenSpacePoint.toArray()],
      boundingBoxOrigin: origin.toArray(),
      boundingBoxSize: this.wb.camera
        .convertScreenToWorld(this.wb.camera.viewportSize)
        .subtract(origin)
        .toArray(),
      boundingBoxOriginalSize: this.wb.camera.viewportSize.toArray(),
      strokeWidth: this.wb.camera.scale * this.shapePrototype.strokeWidth,
    });

    this.wb.shapeStore.sendUpsertMany(Scope.Local, this.shapeID, {
      ...this.shape,
      points: [[this.lastScreenSpacePoint.x, this.lastScreenSpacePoint.y]],
    });

    // Start RAF loop
    this.animationFrameHandle = window.requestAnimationFrame(
      this.handleAnimationFrame
    );
  };

  onPointerMove = (point: Vector) => {
    if (!this.isPointerDown) return;
    this.lastScreenSpacePoint = point;
  };

  handleAnimationFrame = () => {
    if (!this.wb) return;
    if (!this.isPointerDown) return;

    // Request another animation frame
    this.animationFrameHandle = window.requestAnimationFrame(
      this.handleAnimationFrame
    );

    // Ignore this frame if mouse hasn't moved
    if (
      Vector.fromArray(
        this.shape.points[this.shape.points.length - 1]
      ).isEqualTo(this.lastScreenSpacePoint)
    )
      return;

    // Add the point to the list
    this.shape.points.push([
      this.lastScreenSpacePoint.x,
      this.lastScreenSpacePoint.y,
    ]);

    // Update screen, database, and other clients with latest data
    this.wb.shapeStore.sendAppend(Scope.Local, this.shapeID, {
      key: AttributeKey.Points,
      value: [[this.lastScreenSpacePoint.x, this.lastScreenSpacePoint.y]],
    });
  };

  onPointerUp = () => {
    if (!this.wb) return;
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.lastScreenSpacePoint = new Vector(0, 0);

    const op = new AddPenOperation(
      this.wb.makeID(),
      this.wb.camera,
      this.shape
    );
    this.wb.applyOperation(op);

    this.wb.shapeStore.sendDelete(Scope.Local, this.shapeID);

    this.shapeID = "";

    window.cancelAnimationFrame(this.animationFrameHandle);
  };

  abort() {
    this.isPointerDown = false;
    this.lastScreenSpacePoint = new Vector(0, 0);

    this.wb?.shapeStore.sendDelete(Scope.Local, this.shapeID);
    this.shapeID = "";

    window.cancelAnimationFrame(this.animationFrameHandle);
  }

  bind(wb: WhiteboardControllerInterface) {
    this.wb = wb;
    this.eventHandler.bind(wb, this);
  }

  unbind() {
    this.wb = undefined;
    this.eventHandler.unbind();
  }
}

export class MarkerTool extends PenTool {
  constructor() {
    super();
    this.toolType = ToolType.Marker;
  }

  settingsDidChange(settings: WBHUDState) {
    super.settingsDidChange(settings);
    this.shapePrototype.alpha = settings.opacity;
  }
}

export class EraserTool extends PenTool {
  toolType = ToolType.Eraser;

  shapePrototype: PenlikeShape2 = {
    shapeType: ShapeType.Eraser,
    isOpen: true,
    alpha: 1,
    strokeColor: "#ff0000", // ignored
    strokeWidth: 20,
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
    points: [],
  };

  settingsDidChange(settings: WBHUDState) {
    super.settingsDidChange(settings);
    this.shapePrototype.strokeWidth = settings.strokeWidthForEraser;
  }
}
